import React, { useEffect, useMemo, useState } from "react";
import {
  Briefcase,
  Building2,
  Flag,
  Layers,
  RefreshCw,
  Users,
} from "lucide-react";
import Loader from "@/components/Loader";
import MetricCard from "@/components/dashboard/MetricCard";
import ChartWithTypeSwitch from "@/components/dashboard/ChartWithTypeSwitch";
import Dropdown from "@/components/ui/Dropdown";
import ApplicationFilters from "@/components/Companies/ApplicationFilters";
import { companiesService } from "@/services/companiesService";
import { campaignsService } from "@/services/campaignsService";
import { vacancyService } from "@/services/vacancyService";
import { useFoundationMemberApplications } from "@/hooks/queries/useFoundationMembers";

const FILTER_STORAGE_PREFIX = "foundation-members";

const sortChartLabels = (a, b) => {
  const aNum = Number(a.label);
  const bNum = Number(b.label);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
  return b.count - a.count;
};

const aggregateByKey = (applications, key) => {
  const counts = {};
  applications.forEach((app) => {
    const label =
      app?.[key] == null || String(app[key]).trim() === ""
        ? "Unknown"
        : String(app[key]).trim();
    counts[label] = (counts[label] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([label, count]) => ({ label, count }))
    .sort(sortChartLabels);
};

const normalizeApplications = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.applications)) return data.applications;
  if (Array.isArray(data.candidates)) return data.candidates;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const normalizeList = (response, keys = []) => {
  if (Array.isArray(response)) return response;
  for (const key of keys) {
    if (Array.isArray(response?.[key])) return response[key];
    if (Array.isArray(response?.data?.[key])) return response.data[key];
  }
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isFoundationVacancy = (vacancy) =>
  vacancy?.foundation_members === 1 ||
  vacancy?.foundation_members === true ||
  vacancy?.foundationMembers === true;

const normalizeMatchKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const matchesAnyKey = (value, keys) => {
  const normalized = normalizeMatchKey(value);
  if (!normalized) return false;
  return keys.some((key) => normalized.includes(normalizeMatchKey(key)));
};

const parseMaybeJson = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (
    !(trimmed.startsWith("{") || trimmed.startsWith("[")) &&
    !trimmed.startsWith('"')
  ) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const pickFirstNonEmpty = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    return value;
  }
  return null;
};

const getApplicationFields = (app) => {
  if (Array.isArray(app?.application_fields)) return app.application_fields;
  if (Array.isArray(app?.fields)) return app.fields;
  if (Array.isArray(app?.custom_fields)) return app.custom_fields;
  return [];
};

const extractFromSubFields = (field, labelKeys) => {
  const subFields = [
    ...(Array.isArray(field?.application_sub_fields)
      ? field.application_sub_fields
      : []),
    ...(Array.isArray(field?.sub_fields) ? field.sub_fields : []),
  ];

  for (const sub of subFields) {
    const label = sub?.label ?? sub?.field_name ?? sub?.name ?? "";
    if (!matchesAnyKey(label, labelKeys)) continue;
    const value = pickFirstNonEmpty(sub?.value, sub?.field_value);
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
};

const extractGraduationYearFromUniversityValue = (rawValue) => {
  const parsed = parseMaybeJson(rawValue);
  if (!parsed) return null;

  const entries = Array.isArray(parsed) ? parsed : [parsed];
  const years = entries
    .map((entry) =>
      pickFirstNonEmpty(
        entry?.graduationYear,
        entry?.graduation_year,
        entry?.grad_year,
        entry?.year_of_graduation,
        entry?.year,
      ),
    )
    .filter((year) => year != null && String(year).trim() !== "")
    .map((year) => String(year).trim());

  if (years.length === 0) return null;

  const numericYears = years
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year));

  if (numericYears.length > 0) {
    return String(Math.max(...numericYears));
  }

  return years[0];
};

const extractGraduationYear = (app) => {
  const direct = pickFirstNonEmpty(
    app?.graduation_year,
    app?.grad_year,
    app?.year_of_graduation,
    app?.graduationYear,
    app?.candidate?.graduation_year,
    app?.candidate?.grad_year,
    app?.university?.graduation_year,
    app?.university?.graduationYear,
  );
  if (direct != null) return String(direct).trim();

  const graduationLabelKeys = [
    "graduation year",
    "graduation_year",
    "grad year",
    "year of graduation",
  ];
  const fields = getApplicationFields(app);

  for (const field of fields) {
    const fieldName = field?.field_name ?? field?.label ?? field?.name ?? "";
    const fieldType = String(field?.field_type || "").toLowerCase();

    if (
      fieldType === "university_repeatable" ||
      matchesAnyKey(fieldName, [
        "university",
        "education",
        "degree",
        "academic",
      ])
    ) {
      const fromSubs = extractFromSubFields(field, graduationLabelKeys);
      if (fromSubs) return fromSubs;

      const fromValue = extractGraduationYearFromUniversityValue(
        field?.field_value ?? field?.value,
      );
      if (fromValue) return fromValue;
    }

    if (matchesAnyKey(fieldName, graduationLabelKeys)) {
      const value = pickFirstNonEmpty(field?.field_value, field?.value);
      if (value != null && String(value).trim() !== "") {
        return String(value).trim();
      }
    }
  }

  // Fallback: any sub-field labeled as graduation year
  for (const field of fields) {
    const fromSubs = extractFromSubFields(field, graduationLabelKeys);
    if (fromSubs) return fromSubs;
  }

  return null;
};

const isGenderFieldName = (value) => {
  const normalized = normalizeMatchKey(value);
  if (!normalized) return false;
  return (
    normalized === "sex" ||
    normalized === "gender" ||
    normalized.includes("gender")
  );
};

const extractGender = (app) => {
  const direct = pickFirstNonEmpty(
    app?.gender,
    app?.Gender,
    app?.candidate?.gender,
    app?.candidate?.Gender,
    app?.personal?.gender,
    app?.custom_fields?.gender,
    app?.custom_fields?.Gender,
  );
  if (direct != null) return String(direct).trim();

  const fields = getApplicationFields(app);

  for (const field of fields) {
    const fieldName = field?.field_name ?? field?.label ?? field?.name ?? "";
    if (!isGenderFieldName(fieldName)) continue;

    const fromSubs = extractFromSubFields(field, ["gender"]);
    if (fromSubs) return fromSubs;

    const value = pickFirstNonEmpty(field?.field_value, field?.value);
    if (value != null && String(value).trim() !== "") {
      const parsed = parseMaybeJson(value);
      if (Array.isArray(parsed)) {
        const first = parsed.find(
          (item) => item != null && String(item).trim() !== "",
        );
        if (first != null) return String(first).trim();
      }
      if (parsed != null && typeof parsed === "object") {
        const nested = pickFirstNonEmpty(
          parsed?.label,
          parsed?.value,
          parsed?.name,
        );
        if (nested != null) return String(nested).trim();
      }
      return String(value).trim();
    }
  }

  for (const field of fields) {
    const fromSubs = extractFromSubFields(field, ["gender"]);
    if (fromSubs) return fromSubs;
  }

  return null;
};

const extractSchoolYear = (app) => {
  const direct = pickFirstNonEmpty(
    app?.school_year,
    app?.schoolYear,
    app?.year_of_study,
    app?.academic_year,
    app?.academicYear,
    app?.candidate?.school_year,
    app?.candidate?.schoolYear,
    app?.candidate?.year_of_study,
    app?.custom_fields?.school_year,
    app?.custom_fields?.schoolYear,
  );
  if (direct != null) return String(direct).trim();

  const schoolYearKeys = [
    "school year",
    "school_year",
    "year of study",
    "year_of_study",
    "academic year",
    "academic_year",
    "current year",
  ];
  const fields = getApplicationFields(app);

  for (const field of fields) {
    const fieldName = field?.field_name ?? field?.label ?? field?.name ?? "";
    if (!matchesAnyKey(fieldName, schoolYearKeys)) continue;

    const fromSubs = extractFromSubFields(field, schoolYearKeys);
    if (fromSubs) return fromSubs;

    const value = pickFirstNonEmpty(field?.field_value, field?.value);
    if (value != null && String(value).trim() !== "") {
      const parsed = parseMaybeJson(value);
      if (Array.isArray(parsed)) {
        const first = parsed.find(
          (item) => item != null && String(item).trim() !== "",
        );
        if (first != null) return String(first).trim();
      }
      if (parsed != null && typeof parsed === "object") {
        const nested = pickFirstNonEmpty(
          parsed?.label,
          parsed?.value,
          parsed?.name,
          parsed?.schoolYear,
          parsed?.school_year,
        );
        if (nested != null) return String(nested).trim();
      }
      return String(value).trim();
    }
  }

  for (const field of fields) {
    const fromSubs = extractFromSubFields(field, schoolYearKeys);
    if (fromSubs) return fromSubs;

    const fieldType = String(field?.field_type || "").toLowerCase();
    if (fieldType === "university_repeatable") {
      const parsed = parseMaybeJson(field?.field_value ?? field?.value);
      const entries = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
      for (const entry of entries) {
        const year = pickFirstNonEmpty(
          entry?.schoolYear,
          entry?.school_year,
          entry?.year_of_study,
          entry?.academic_year,
        );
        if (year != null && String(year).trim() !== "") {
          return String(year).trim();
        }
      }
    }
  }

  return null;
};

const FoundationMembersReport = () => {
  const [companies, setCompanies] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingVacancies, setIsLoadingVacancies] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeFilters, setActiveFilters] = useState([]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        const response = await companiesService.getCompaniesList({
          params: {
            page: 1,
            per_page: 100,
          },
        });
        setCompanies(normalizeList(response, ["companies", "data"]));
      } catch (error) {
        console.error("Error fetching companies:", error);
        setCompanies([]);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!selectedCompany?.value) {
        setCampaigns([]);
        return;
      }

      try {
        setIsLoadingCampaigns(true);
        const response = await campaignsService.getCampaignByCompanyId(
          selectedCompany.value,
        );
        const list = normalizeList(response, ["campaigns"]);
        setCampaigns(Array.isArray(list) ? list : list?.campaigns || []);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setCampaigns([]);
      } finally {
        setIsLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, [selectedCompany?.value]);

  useEffect(() => {
    const fetchVacancies = async () => {
      if (!selectedCompany?.value || !selectedCampaign?.value) {
        setVacancies([]);
        return;
      }

      try {
        setIsLoadingVacancies(true);
        const response = await vacancyService.getVacancies(
          selectedCompany.value,
          {
            campaign_id: selectedCampaign.value,
            status: [0, 1],
          },
        );
        const list = normalizeList(response, ["vacancies"]);
        setVacancies(list.filter(isFoundationVacancy));
      } catch (error) {
        console.error("Error fetching vacancies:", error);
        setVacancies([]);
      } finally {
        setIsLoadingVacancies(false);
      }
    };

    fetchVacancies();
  }, [selectedCompany?.value, selectedCampaign?.value]);

  const handleCompanyChange = (selected) => {
    setSelectedCompany(selected || null);
    setSelectedCampaign(null);
    setSelectedVacancy(null);
    setCampaigns([]);
    setVacancies([]);
  };

  const handleCampaignChange = (selected) => {
    setSelectedCampaign(selected || null);
    setSelectedVacancy(null);
    setVacancies([]);
  };

  const handleVacancyChange = (selected) => {
    setSelectedVacancy(selected || null);
  };

  const queryParams = useMemo(() => {
    const params = {
      page: 1,
      per_page: 1000,
    };
    if (selectedCompany?.value) params.company_id = selectedCompany.value;
    if (selectedCampaign?.value) params.campaign_id = selectedCampaign.value;
    if (selectedVacancy?.value) params.vacancy_id = selectedVacancy.value;
    return params;
  }, [selectedCompany?.value, selectedCampaign?.value, selectedVacancy?.value]);

  const hasVacancySelected = Boolean(selectedVacancy?.value);

  const { data, isLoading, isError, error } = useFoundationMemberApplications(
    queryParams,
    { enabled: hasVacancySelected },
  );

  const applications = useMemo(
    () => (hasVacancySelected ? normalizeApplications(data) : []),
    [data, hasVacancySelected],
  );

  const enrichedApplications = useMemo(
    () =>
      applications.map((app) => ({
        ...app,
        graduation_year: extractGraduationYear(app) || "Unknown",
        school_year: extractSchoolYear(app) || "Unknown",
        gender: extractGender(app) || "Unknown",
      })),
    [applications],
  );

  const filterColumns = useMemo(() => {
    const years = [
      ...new Set(enrichedApplications.map((app) => app.graduation_year)),
    ].sort((a, b) => {
      const aNum = Number(a);
      const bNum = Number(b);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
      return String(a).localeCompare(String(b));
    });

    const schoolYears = [
      ...new Set(enrichedApplications.map((app) => app.school_year)),
    ].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

    const genders = [
      ...new Set(enrichedApplications.map((app) => app.gender)),
    ].sort((a, b) => String(a).localeCompare(String(b)));

    return [
      {
        key: "graduation_year",
        label: "Graduation Year",
        filterable: true,
        inputType: "select",
        useMultiSelect: true,
        options: years,
      },
      {
        key: "school_year",
        label: "School Year",
        filterable: true,
        inputType: "select",
        useMultiSelect: true,
        options: schoolYears,
      },
      {
        key: "gender",
        label: "Gender",
        filterable: true,
        inputType: "select",
        useMultiSelect: true,
        options: genders,
      },
    ];
  }, [enrichedApplications]);

  const visibleFilterColumns = useMemo(
    () => ({
      graduation_year: true,
      school_year: true,
      gender: true,
    }),
    [],
  );

  const filteredApplications = useMemo(() => {
    if (!activeFilters.length) return enrichedApplications;

    return enrichedApplications.filter((app) =>
      activeFilters.every((filterKey) => {
        const selected = filters[filterKey];
        if (
          selected == null ||
          selected === "" ||
          (Array.isArray(selected) && selected.length === 0)
        ) {
          return true;
        }

        const selectedValues = Array.isArray(selected) ? selected : [selected];
        const appValue = String(app[filterKey] ?? "").toLowerCase();
        return selectedValues.some(
          (value) => String(value).toLowerCase() === appValue,
        );
      }),
    );
  }, [enrichedApplications, filters, activeFilters]);

  const hasActiveFilterValues = useMemo(
    () =>
      activeFilters.some((key) => {
        const selected = filters[key];
        return Array.isArray(selected)
          ? selected.length > 0
          : selected != null && selected !== "";
      }),
    [filters, activeFilters],
  );

  const selectionSteps = [
    {
      label: "Company",
      done: Boolean(selectedCompany?.value),
      icon: Building2,
    },
    {
      label: "Campaign",
      done: Boolean(selectedCampaign?.value),
      icon: Layers,
    },
    {
      label: "Vacancy",
      done: hasVacancySelected,
      icon: Briefcase,
    },
  ];

  const nextSelectionHint = !selectedCompany?.value
    ? "Start by selecting a company from the filters above."
    : !selectedCampaign?.value
      ? "Next, select a campaign to continue."
      : "Almost there — select a vacancy to view the report.";

  const graduationYearData = useMemo(
    () => aggregateByKey(filteredApplications, "graduation_year"),
    [filteredApplications],
  );

  const genderData = useMemo(
    () => aggregateByKey(filteredApplications, "gender"),
    [filteredApplications],
  );

  const totalStudents = hasActiveFilterValues
    ? filteredApplications.length
    : Number(
        data?.totalApplications ?? data?.total ?? data?.pagination?.total,
      ) || filteredApplications.length;

  return (
    <div className="w-[90%] mx-auto pt-12 pb-24 px-8 flex flex-col gap-8">
      <div className="self-stretch flex flex-col justify-start items-start gap-4">
        <div className="flex-1 min-w-80 inline-flex flex-col justify-start items-start gap-1">
          <div className="self-stretch justify-start text-foreground text-2xl font-semibold font-['Inter'] leading-8">
            Foundation Members Report
          </div>
          <div className="self-stretch justify-start text-muted-foreground text-base font-normal font-['Inter'] leading-6">
            Charts showing how many students applied by graduation year and
            gender.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-start gap-4 bg-card p-4 rounded-lg border border-border">
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-foreground mb-1">
            Company
          </label>
          <div className="flex items-center gap-2">
            <Dropdown
              options={companies.map((company) => ({
                label: company.name,
                value: company.id,
              }))}
              value={selectedCompany}
              onChange={handleCompanyChange}
              placeholder={
                isLoadingCompanies ? "Loading companies..." : "Select Company"
              }
              icon={
                <Building2 className="w-5 h-5 text-foreground flex-shrink-0" />
              }
              searchable={true}
              className="min-h-[50px] w-auto min-w-[260px] border border-border rounded-lg"
              dropdownWidth="w-auto min-w-[30%]"
              disabled={isLoadingCompanies}
            />
            {selectedCompany && (
              <button
                type="button"
                className="p-1 rounded hover:bg-muted focus:outline-none"
                onClick={() => handleCompanyChange(null)}
                title="Reset Company Filter"
              >
                <RefreshCw className="w-5 h-5 text-foreground" />
              </button>
            )}
          </div>
        </div>

        {selectedCompany && (
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-foreground mb-1">
              Campaign
            </label>
            <div className="flex items-center gap-2">
              <Dropdown
                options={campaigns.map((campaign) => ({
                  label: campaign.title,
                  value: campaign.id,
                }))}
                value={selectedCampaign}
                onChange={handleCampaignChange}
                placeholder={
                  isLoadingCampaigns
                    ? "Loading campaigns..."
                    : campaigns.length > 0
                      ? "Select Campaign"
                      : "No Campaigns"
                }
                icon={
                  <Building2 className="w-5 h-5 text-foreground flex-shrink-0" />
                }
                searchable={true}
                className="min-h-[50px] w-auto min-w-[260px] border border-border rounded-lg"
                dropdownWidth="w-auto min-w-[30%]"
                disabled={isLoadingCampaigns || campaigns.length === 0}
              />
              {selectedCampaign && (
                <button
                  type="button"
                  className="p-1 rounded hover:bg-muted focus:outline-none"
                  onClick={() => handleCampaignChange(null)}
                  title="Reset Campaign Filter"
                >
                  <RefreshCw className="w-5 h-5 text-foreground" />
                </button>
              )}
            </div>
          </div>
        )}

        {selectedCampaign && (
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-foreground mb-1">
              Vacancy
            </label>
            <div className="flex items-center gap-2">
              <Dropdown
                options={
                  vacancies.length > 0
                    ? vacancies.map((vacancy) => ({
                        label: vacancy.title,
                        value: vacancy.id,
                      }))
                    : [
                        {
                          label: "No Foundation Vacancies",
                          value: "no_vacancies",
                          disabled: true,
                        },
                      ]
                }
                value={selectedVacancy}
                onChange={handleVacancyChange}
                placeholder={
                  isLoadingVacancies
                    ? "Loading vacancies..."
                    : vacancies.length > 0
                      ? "Select Vacancy"
                      : "No Foundation Vacancies"
                }
                icon={
                  <Briefcase className="w-5 h-5 text-foreground flex-shrink-0" />
                }
                searchable={true}
                className="min-h-[50px] w-auto min-w-[260px] border border-border rounded-lg"
                dropdownWidth="w-auto min-w-[30%]"
                disabled={isLoadingVacancies || vacancies.length === 0}
                renderOption={(option) => (
                  <p
                    className={`text-foreground ${option.disabled ? "opacity-50" : ""}`}
                  >
                    {option.label}
                  </p>
                )}
              />
              {selectedVacancy && (
                <button
                  type="button"
                  className="p-1 rounded hover:bg-muted focus:outline-none"
                  onClick={() => handleVacancyChange(null)}
                  title="Reset Vacancy Filter"
                >
                  <RefreshCw className="w-5 h-5 text-foreground" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {!hasVacancySelected ? (
        <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-card via-card to-primary-dark/5 px-6 py-16 sm:px-10">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary-dark/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-secondary/10 blur-3xl" />

          <div className="relative mx-auto flex max-w-2xl flex-col items-center text-center gap-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-dark text-white shadow-lg shadow-primary-dark/20">
              <Flag className="h-7 w-7 fill-white/90" strokeWidth={2} />
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-foreground tracking-tight">
                Select filters to view report
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                Please select a <span className="font-medium text-foreground">Company</span>,{" "}
                <span className="font-medium text-foreground">Campaign</span>, and{" "}
                <span className="font-medium text-foreground">Vacancy</span> above
                to load Foundation Members analytics.
              </p>
              <p className="text-sm text-muted-foreground/90">{nextSelectionHint}</p>
            </div>

            <div className="mt-2 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
              {selectionSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.label}
                    className={`flex flex-1 items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                      step.done
                        ? "border-primary-dark/30 bg-primary-dark/5"
                        : "border-border bg-card"
                    }`}
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                        step.done
                          ? "bg-primary-dark text-white"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {step.done ? (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </span>
                    <div className="text-left leading-tight">
                      <p className="text-xs text-muted-foreground">
                        Step {index + 1}
                      </p>
                      <p
                        className={`text-sm font-semibold ${
                          step.done ? "text-primary-dark" : "text-foreground"
                        }`}
                      >
                        {step.label}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <>
          {isError && (
            <div className="p-4 bg-red-100 text-red-700 rounded-md border border-red-200">
              {error?.message || "Failed to load foundation member analytics."}
            </div>
          )}

          {isLoading ? (
            <div className="py-24 flex flex-col items-center justify-center">
              <Loader
                className="w-8 h-8"
                bgColor="bg-transparent"
                loadingText="Loading analytics..."
              />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <ApplicationFilters
                  key={selectedVacancy?.value || "no-vacancy"}
                  columns={filterColumns}
                  visibleColumns={visibleFilterColumns}
                  processedApplications={enrichedApplications}
                  onFiltersChange={(nextFilters, nextActiveFilters) => {
                    setFilters(nextFilters || {});
                    setActiveFilters(nextActiveFilters || []);
                  }}
                  filtersStorageKey={`${FILTER_STORAGE_PREFIX}-filters-${selectedVacancy?.value}`}
                  activeFiltersStorageKey={`${FILTER_STORAGE_PREFIX}-activeFilters-${selectedVacancy?.value}`}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard
                  icon={<Users className="w-4 h-4 text-primary-dark" />}
                  title="Total Students"
                  value={totalStudents}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ChartWithTypeSwitch
                  title="Students by Graduation Year"
                  data={graduationYearData}
                />
                <ChartWithTypeSwitch
                  title="Students by gender"
                  data={genderData}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default FoundationMembersReport;
